// START IMPORTS
import { Box, Stack, Text } from '@ltvco/refresh-lib/theme';
import { IconContainer } from '@ltvco/refresh-lib/v2';

import { isEventTargetInsideInteractiveControl } from 'utils/dom/isEventTargetInsideInteractiveControl';

import { renderOptionalMutedText } from '../../../utils';

import {
  ReportListItemRoot,
  trailingTextButtonSlotSx,
} from './ReportListItem.styles';

import type { ReportListItemProps } from './types';
import type { KeyboardEvent, MouseEvent } from 'react';
// END IMPORTS

/**
 * Card shell for a single report row. Bucket (recent / saved / custom / monitoring) is chosen by the
 * page; pass product type via `subtitle` and the primary subject via `title`.
 */
export function ReportListItem({
  icon,
  title,
  detail,
  subtitle,
  metadata,
  footer,
  trailing,
  trailingPlacement = 'card-end',
  sx,
  onCardClick,
}: ReportListItemProps) {
  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onCardClick) {
      return;
    }
    if (
      isEventTargetInsideInteractiveControl(event.target) &&
      event.target !== event.currentTarget
    ) {
      return;
    }
    onCardClick();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onCardClick) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (
      isEventTargetInsideInteractiveControl(event.target) &&
      event.target !== event.currentTarget
    ) {
      return;
    }
    event.preventDefault();
    onCardClick();
  };

  const titleBlock = (
    <Stack
      spacing={0.75}
      minWidth={0}
      sx={trailingPlacement === 'beside-title' ? { flex: 1 } : undefined}
    >
      <Text
        component="div"
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          lineHeight: 1.35,
          color: 'text.primary',
          fontSize: { xs: '1.0625rem', sm: '1.125rem' },
        }}
      >
        {title}
      </Text>
      {renderOptionalMutedText(detail)}
      {renderOptionalMutedText(subtitle)}
    </Stack>
  );

  const mainColumn = (
    <Stack spacing={1.5} flex={1} minWidth={0}>
      {trailingPlacement === 'beside-title' ? (
        <Stack direction="row" alignItems="center" spacing={2} minWidth={0}>
          {titleBlock}
          {trailing !== null && trailing !== undefined ? (
            <Box sx={{ flexShrink: 0, ...trailingTextButtonSlotSx }}>
              {trailing}
            </Box>
          ) : null}
        </Stack>
      ) : (
        titleBlock
      )}
      {metadata}
      {footer}
    </Stack>
  );

  return (
    <ReportListItemRoot
      clickable={Boolean(onCardClick)}
      onClick={onCardClick ? handleCardClick : undefined}
      onKeyDown={onCardClick ? handleCardKeyDown : undefined}
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      aria-label={onCardClick ? title : undefined}
      sx={sx}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems={
          trailingPlacement === 'beside-title' ? 'flex-start' : 'stretch'
        }
      >
        {icon !== null && icon !== undefined ? (
          <Box sx={{ flexShrink: 0, alignSelf: 'flex-start', lineHeight: 0 }}>
            <IconContainer icon={icon} />
          </Box>
        ) : null}
        {mainColumn}
        {trailingPlacement === 'card-end' &&
        trailing !== null &&
        trailing !== undefined ? (
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start',
              alignSelf: 'flex-start',
              pl: 1,
              ...trailingTextButtonSlotSx,
            }}
          >
            {trailing}
          </Box>
        ) : null}
      </Stack>
    </ReportListItemRoot>
  );
}
